import { createRoot } from "react-dom/client";
import { HelmetProvider } from 'react-helmet-async';
import App from "./App.tsx";
import LenisProvider from "./lib/lenis";
import { initAnimatedFavicon } from "./lib/animatedFavicon";
import "./i18n";
import "./index.css";
import "./lib/webmcp";

// Fonts loaded asynchronously after critical CSS
// This prevents render-blocking while ensuring fonts are available

// Primary Arabic + Latin — Zain (modern geometric, brutalist-friendly, full
// weight range from light to black). Drives body, display, hero, and the
// mono-label fallback for Arabic content.
import '@fontsource/zain/400.css';
import '@fontsource/zain/700.css';
import '@fontsource/zain/800.css';
import '@fontsource/zain/900.css';
// Legacy Arabic fallbacks — kept for unicode coverage if Zain's subset
// misses a glyph in less-common scripts.
import '@fontsource/noto-kufi-arabic/400.css';
import '@fontsource/noto-kufi-arabic/700.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/700.css';
// v2 marketing display fonts — IBM Plex Sans Arabic + Space Grotesk (max weight 700 available)
import '@fontsource/ibm-plex-sans-arabic/400.css';
import '@fontsource/ibm-plex-sans-arabic/700.css';
import '@fontsource/space-grotesk/500.css';
import '@fontsource/space-grotesk/700.css';
// English font — Inter (loaded for both languages; active font swapped via body class)
import '@fontsource/inter/400.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

createRoot(document.getElementById("root")!).render(
    <HelmetProvider>
        <LenisProvider>
            <App />
        </LenisProvider>
    </HelmetProvider>
);

// Defer the animated-favicon boot until the browser is idle so it never
// competes with first paint or hydration.
const startFavicon = () => initAnimatedFavicon();
if ("requestIdleCallback" in window) {
    (window as Window & {
        requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void;
    }).requestIdleCallback(startFavicon, { timeout: 2000 });
} else {
    setTimeout(startFavicon, 1500);
}
